import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AddressTable from '../restaurant/AddressTable';
import CompanyTaxesDisplay from '../restaurant/CompanyTaxesDisplay';
import OrderNumberingDisplay from '../restaurant/OrderNumberingDisplay';
import {
  normalizeAdminEntityStatus,
  isInactiveEntityStatus,
} from '../restaurant/formatDisplay';
import { canEditRestaurantStoreProfile } from '../restaurant/deliveryAgentSession';
import RestaurantDashboardLayout from '../restaurant/RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from '../restaurant/useRestaurantEnabledServices';
import { confirmAction } from '../restaurant/ui/feedback';
import EatsAdminPageHeader from '../admin/EatsAdminPageHeader';
import '../restaurant/css/Restaurants.css';
import '../restaurant/css/OrderDetails.css';
import '../admin/EatsAdminDashboard.css';
import '../admin/EatsAdminCreatePage.css';
import {
  extractStoreName,
  fetchStoreById,
  fetchStoreRootAdmin,
  updateStore,
  updateStoreAddress,
  deleteStore,
  fetchStoreMyeatsDeliverySlots,
  updateStoreMyeatsDeliverySlots,
  fetchStoreTimings,
  updateStoreTimings,
} from './storeApi';
import { updateStoreUser } from '../restaurant/storeUserApi';
import OwnerDetailsSection from '../restaurant/OwnerDetailsSection';
import DeliverySlotsEditor, {
  emptyDeliverySlots,
  normalizeDeliverySlots,
  normalizeSlotDurationMinutes,
  DEFAULT_SLOT_DURATION_MINUTES,
} from '../restaurant/DeliverySlotsEditor';
import KitchenMediaEditor from '../restaurant/KitchenMediaEditor';
import StoreOpeningHoursEditor, {
  emptyStoreOpeningHours,
  normalizeStoreOpeningHours,
} from './StoreOpeningHoursEditor';

const ADMIN_STORES_PATH = '/eats/admin?tab=stores';

const SectionHeadingActions = ({
  canEdit,
  editing,
  onEdit,
  onCancel,
  onSave,
  saving = false,
}) => {
  if (!canEdit) return null;
  if (!editing) {
    return (
      <button type="button" className="eats-admin-header-btn" onClick={onEdit}>
        Edit
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        className="eats-admin-header-btn"
        onClick={onCancel}
        disabled={saving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="eats-admin-header-btn"
        onClick={onSave}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </>
  );
};

const StoreDetail = ({ storeId: storeIdProp, embeddedInAdmin = false }) => {
  const { storeId: routeStoreId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeId = storeIdProp ?? routeStoreId;
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const readOnlyView = searchParams.get('view') === 'readonly';
  const canManageProfile = canEditRestaurantStoreProfile() && !readOnlyView;
  // Core store fields: Eats admin only. Delivery schedule: store admin + Eats admin.
  const canEdit = Boolean(embeddedInAdmin) && canManageProfile;
  const canEditSchedule = canManageProfile;
  const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
    useRestaurantEnabledServices();

  const [store, setStore] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [storeName, setStoreName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [isDefault, setIsDefault] = useState('');
  const [homeDeliveryAvailable, setHomeDeliveryAvailable] = useState('');
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerReloadToken, setOwnerReloadToken] = useState(0);
  const [deliveryMode, setDeliveryMode] = useState('REGULAR');
  const [deliverySlots, setDeliverySlots] = useState(() => emptyDeliverySlots());
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(DEFAULT_SLOT_DURATION_MINUTES);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [openingHours, setOpeningHours] = useState(() => emptyStoreOpeningHours());
  const [isEditingTimings, setIsEditingTimings] = useState(false);
  const [savingTimings, setSavingTimings] = useState(false);
  const initialTab = searchParams.get('tab') || 'details';
  const [activeTab, setActiveTab] = useState(initialTab);

  const applyStoreToForm = useCallback((data) => {
    if (!data) return;
    setStoreName(data.storeName ?? extractStoreName(data));
    setShortCode(data.shortCode ?? '');
    const contact = data.contactNo;
    setContactNo(contact !== '' && contact != null ? String(contact) : '');
    setEmail(data.email ?? '');
    setNotes(data.notes ?? '');
    setStatus(normalizeAdminEntityStatus(data.status ?? ''));
    const def = data.isDefault;
    setIsDefault(def !== undefined && def !== null ? String(def) : '');
    const hd = data.homeDeliveryAvailable;
    setHomeDeliveryAvailable(hd !== undefined && hd !== null ? String(hd) : '');
    setAddresses(data.addresses ?? []);
  }, []);

  const loadStore = useCallback(async () => {
    const id = storeId != null ? String(storeId).trim() : '';
    if (!id || id === 'null' || id === 'undefined') {
      setError('Invalid store id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await fetchStoreById(id);
      setStore(data);
      applyStoreToForm(data);
      try {
        const slotsPayload = await fetchStoreMyeatsDeliverySlots(id);
        setDeliveryMode(slotsPayload?.deliveryMode || 'REGULAR');
        setDeliverySlots(normalizeDeliverySlots(slotsPayload?.deliverySlots));
        setSlotDurationMinutes(
          normalizeSlotDurationMinutes(
            slotsPayload?.slotDurationMinutes,
            DEFAULT_SLOT_DURATION_MINUTES,
          ),
        );
      } catch (slotsError) {
        console.warn('Store delivery slots fetch failed:', slotsError);
        setDeliveryMode('REGULAR');
        setDeliverySlots(emptyDeliverySlots());
        setSlotDurationMinutes(DEFAULT_SLOT_DURATION_MINUTES);
      }
      try {
        const timingsPayload = await fetchStoreTimings(id);
        setOpeningHours(normalizeStoreOpeningHours(timingsPayload?.openingHours));
      } catch (timingsError) {
        console.warn('Store timings fetch failed:', timingsError);
        setOpeningHours(emptyStoreOpeningHours());
      }
    } catch (e) {
      console.error('Store detail fetch failed:', e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load store details.'
      );
    } finally {
      setLoading(false);
    }
  }, [storeId, applyStoreToForm]);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  useEffect(() => {
    if (!embeddedInAdmin || !storeId) {
      setOwnerDetails(null);
      setOwnerLoading(false);
      return;
    }

    const id = String(storeId).trim();
    if (!id || id === 'null' || id === 'undefined') {
      setOwnerDetails(null);
      return;
    }

    let cancelled = false;
    setOwnerLoading(true);
    fetchStoreRootAdmin(id)
      .then((owner) => {
        if (!cancelled) setOwnerDetails(owner);
      })
      .catch((ownerError) => {
        console.warn('Store owner fetch failed:', ownerError);
        if (!cancelled) setOwnerDetails(null);
      })
      .finally(() => {
        if (!cancelled) setOwnerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [embeddedInAdmin, storeId, ownerReloadToken]);

  const handleSaveOwner = async (payload) => {
    if (!ownerDetails?.id) {
      throw new Error('Owner is not assigned.');
    }
    await updateStoreUser(ownerDetails.id, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      mobile: payload.mobile,
      aadharNo: payload.aadharNo,
      storeId,
      storeName: storeName || extractStoreName(store),
      updatedById: user?.userId ?? 1,
    });
    setOwnerReloadToken((token) => token + 1);
    window.alert('Owner updated successfully');
  };

  const handleUpdate = async () => {
    if (!storeId) return;

    const contactDigits = String(contactNo || '').replace(/\D/g, '');
    if (contactDigits.length !== 10) {
      window.alert('Please enter a valid 10-digit contact number.');
      return;
    }

    setSaving(true);
    try {
      const result = await updateStore(
        storeId,
        {
          storeName,
          shortCode,
          contactNo,
          email,
          notes,
          isDefault,
          homeDeliveryAvailable,
          status,
        },
        user?.userId ?? 1
      );

      alert(result?.message || 'Store updated successfully');
      setIsEditing(false);
      await loadStore();
    } catch (e) {
      console.error('Update store error:', e);
      alert(e?.response?.data?.message || 'Failed to update store');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!storeId) return;

    const ok = await confirmAction({
      title: 'Delete store?',
      message: 'Are you sure you want to delete this store? This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      await deleteStore(storeId);
      alert('Store deactivated successfully');
      navigate(embeddedInAdmin ? ADMIN_STORES_PATH : '/eats/admin?tab=stores');
    } catch (e) {
      console.error('Delete store error:', e);
      alert(e?.response?.data?.message || 'Failed to deactivate store');
    }
  };

  const handleAddressUpdate = async (index, updatedAddress) => {
    if (!updatedAddress.addressId) {
      alert('Cannot update: missing address id');
      return;
    }

    try {
      await updateStoreAddress(updatedAddress.addressId, updatedAddress, user?.userId ?? 1);
      const updated = [...addresses];
      updated[index] = updatedAddress;
      setAddresses(updated);
      alert('Address updated successfully');
      await loadStore();
    } catch (e) {
      console.error('Update address error:', e);
      alert(e?.response?.data?.message || 'Failed to update address');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (store) applyStoreToForm(store);
  };

  const handleSaveSchedule = async () => {
    if (!storeId) return;
    setSavingSchedule(true);
    try {
      await updateStoreMyeatsDeliverySlots(
        storeId,
        { deliveryMode, deliverySlots, slotDurationMinutes },
        user?.userId ?? 1,
      );
      setIsEditingSchedule(false);
      await loadStore();
      alert('Delivery options updated successfully');
    } catch (e) {
      console.error('Store delivery options update failed:', e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to update delivery options',
      );
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveTimings = async () => {
    if (!storeId) return;
    setSavingTimings(true);
    try {
      await updateStoreTimings(
        storeId,
        { openingHours },
        user?.userId ?? 1,
      );
      setIsEditingTimings(false);
      await loadStore();
      alert('Store timings updated successfully');
    } catch (e) {
      console.error('Store timings update failed:', e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to update store timings',
      );
    } finally {
      setSavingTimings(false);
    }
  };

  const displayName = extractStoreName(store) || storeName || 'Store details';

  const detailTabs = useMemo(() => {
    const tabs = [
      { id: 'details', label: 'Details' },
      { id: 'timings', label: 'Timings' },
      { id: 'deliveryOptions', label: 'Delivery Options' },
      { id: 'media', label: 'Media' },
    ];
    if (embeddedInAdmin) {
      tabs.push({ id: 'owner', label: 'Owner' });
    }
    tabs.push(
      { id: 'address', label: 'Address' },
      { id: 'taxes', label: 'Taxes' },
      { id: 'orderNumbering', label: 'Order Numbering' },
    );
    return tabs;
  }, [embeddedInAdmin]);

  useEffect(() => {
    if (!detailTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('details');
    }
  }, [activeTab, detailTabs]);

  useEffect(() => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl && detailTabs.some((tab) => tab.id === fromUrl) && fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
  }, [activeTab, detailTabs, searchParams]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    if (isEditing) {
      setIsEditing(false);
      if (store) applyStoreToForm(store);
    }
    if (isEditingSchedule) {
      setIsEditingSchedule(false);
      loadStore();
    }
    if (isEditingTimings) {
      setIsEditingTimings(false);
      loadStore();
    }
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId && tabId !== 'details') {
      next.set('tab', tabId);
    } else {
      next.delete('tab');
    }
    setSearchParams(next, { replace: true });
  };

  const displayIsDefault =
    isDefault === 'true' || isDefault === true || isDefault === '1' || isDefault === 1
      ? 'Yes'
      : isDefault === 'false' || isDefault === false || isDefault === '0' || isDefault === 0
        ? 'No'
        : '—';

  const displayHomeDelivery =
    homeDeliveryAvailable === 'true' ||
    homeDeliveryAvailable === true ||
    homeDeliveryAvailable === '1' ||
    homeDeliveryAvailable === 1
      ? 'Yes'
      : homeDeliveryAvailable === 'false' ||
          homeDeliveryAvailable === false ||
          homeDeliveryAvailable === '0' ||
          homeDeliveryAvailable === 0
        ? 'No'
        : '—';

  const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
  };

  const DetailField = ({ label, value, wide = false, badge = false }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
      <span className="restaurant-detail-label">{label}</span>
      {badge ? (
        <span
          className={`restaurant-detail-badge restaurant-detail-badge-${String(value || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-') || 'unknown'}`}
        >
          {displayValue(value)}
        </span>
      ) : (
        <span className="restaurant-detail-value">{displayValue(value)}</span>
      )}
    </div>
  );

  const handleBack = () => {
    if (embeddedInAdmin) {
      navigate(ADMIN_STORES_PATH);
      return;
    }
    navigate('/business/admin');
  };

  const page = (
    <div
      className={`order-details restaurant-detail-page restaurant-detail-page-admin${
        !embeddedInAdmin ? ' restaurant-detail-page--shell' : ''
      }`}
    >
      <div className="restaurant-detail-panel">
        {embeddedInAdmin ? (
          <EatsAdminPageHeader
            title={displayName}
            onBack={handleBack}
            actions={
              store &&
              !loading &&
              canEdit &&
              activeTab === 'details' &&
              !isInactiveEntityStatus(status) ? (
                <button type="button" className="eats-admin-header-btn" onClick={handleDelete}>
                  Delete
                </button>
              ) : null
            }
          />
        ) : null}

        {loading && <p className="restaurant-detail-message">Loading…</p>}
        {!loading && error && <p className="restaurant-detail-message error">{error}</p>}

        {!loading && !error && store && (
          <div className="restaurant-detail-section">
            <div className="restaurant-detail-tabs" role="tablist" aria-label="Store sections">
              <div className="restaurant-detail-tabs__list">
                {detailTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`restaurant-detail-tab${
                      activeTab === tab.id ? ' restaurant-detail-tab--active' : ''
                    }`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {storeId ? (
                <a
                  className="restaurant-detail-tabs__profile-link"
                  href={`/store/${storeId}/profile`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Profile view
                </a>
              ) : null}
            </div>

            {activeTab === 'details' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Details</h3>
                  <div className="restaurant-detail-card-header-actions">
                    {!isEditing && status ? (
                      <span
                        className={`restaurant-detail-badge restaurant-detail-badge-${String(status)
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '-')}`}
                      >
                        {status}
                      </span>
                    ) : null}
                    <SectionHeadingActions
                      canEdit={canEdit}
                      editing={isEditing}
                      onEdit={() => setIsEditing(true)}
                      onCancel={handleCancelEdit}
                      onSave={handleUpdate}
                      saving={saving}
                    />
                  </div>
                </div>

                {isEditing ? (
                  <div className="restaurant-detail-edit-form">
                    <div className="get-restaurant-form-row flex">
                      <div className="get-restaurant-form-group">
                        <label>Store Name</label>
                        <input
                          type="text"
                          value={storeName}
                          onChange={(e) => setStoreName(e.target.value)}
                          readOnly={!embeddedInAdmin}
                          disabled={!embeddedInAdmin}
                          className={embeddedInAdmin ? undefined : 'restaurant-detail-field-locked'}
                        />
                      </div>
                      <div className="get-restaurant-form-group">
                        <label>Short Code</label>
                        <input
                          type="text"
                          value={shortCode}
                          onChange={(e) => setShortCode(e.target.value)}
                          readOnly={!embeddedInAdmin}
                          disabled={!embeddedInAdmin}
                          className={embeddedInAdmin ? undefined : 'restaurant-detail-field-locked'}
                        />
                      </div>
                      <div className="get-restaurant-form-group">
                        <label>Contact Number</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={10}
                          value={contactNo}
                          onChange={(e) =>
                            setContactNo(e.target.value.replace(/\D/g, '').slice(0, 10))
                          }
                        />
                      </div>
                    </div>

                    <div className="get-restaurant-form-row flex">
                      <div className="get-restaurant-form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="get-restaurant-form-group">
                        <label>Is Default</label>
                        <select value={isDefault} onChange={(e) => setIsDefault(e.target.value)}>
                          <option value="">Select</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                      <div className="get-restaurant-form-group">
                        <label>Home Delivery</label>
                        <select
                          value={homeDeliveryAvailable}
                          onChange={(e) => setHomeDeliveryAvailable(e.target.value)}
                        >
                          <option value="">Select</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                    </div>

                    <div className="get-restaurant-form-row flex">
                      <div className="get-restaurant-form-group">
                        <label>Status</label>
                        {embeddedInAdmin ? (
                          <select
                            value={normalizeAdminEntityStatus(status)}
                            onChange={(e) => setStatus(e.target.value)}
                          >
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                          </select>
                        ) : (
                          <input type="text" value={status} readOnly />
                        )}
                      </div>
                      <div className="get-restaurant-form-group get-restaurant-form-group-wide">
                        <label>Notes</label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="restaurant-detail-grid">
                    <DetailField label="Store Name" value={storeName} />
                    <DetailField label="Short Code" value={shortCode} />
                    <DetailField label="Contact Number" value={contactNo} />
                    <DetailField label="Email" value={email} />
                    <DetailField label="Is Default" value={displayIsDefault} />
                    <DetailField label="Home Delivery" value={displayHomeDelivery} />
                    <DetailField label="Status" value={status} />
                    <DetailField label="Notes" value={notes} wide />
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === 'timings' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Timings</h3>
                  <div className="restaurant-detail-card-header-actions">
                    <SectionHeadingActions
                      canEdit={canEditSchedule}
                      editing={isEditingTimings}
                      onEdit={() => setIsEditingTimings(true)}
                      onCancel={() => {
                        setIsEditingTimings(false);
                        loadStore();
                      }}
                      onSave={handleSaveTimings}
                      saving={savingTimings}
                    />
                  </div>
                </div>
                <StoreOpeningHoursEditor
                  value={openingHours}
                  onChange={setOpeningHours}
                  disabled={!isEditingTimings}
                />
              </div>
            ) : null}

            {activeTab === 'deliveryOptions' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Delivery Options</h3>
                  <div className="restaurant-detail-card-header-actions">
                    <SectionHeadingActions
                      canEdit={canEditSchedule}
                      editing={isEditingSchedule}
                      onEdit={() => setIsEditingSchedule(true)}
                      onCancel={() => {
                        setIsEditingSchedule(false);
                        loadStore();
                      }}
                      onSave={handleSaveSchedule}
                      saving={savingSchedule}
                    />
                  </div>
                </div>
                <DeliverySlotsEditor
                  deliveryMode={deliveryMode}
                  deliverySlots={deliverySlots}
                  onModeChange={setDeliveryMode}
                  onSlotsChange={setDeliverySlots}
                  disabled={!isEditingSchedule}
                  showSlotDuration
                  slotDurationMinutes={slotDurationMinutes}
                  onSlotDurationChange={setSlotDurationMinutes}
                  title="Grocery delivery mode"
                  hint="Regular = ASAP. Fixed = start times with a shared window duration (for batched stop-by-stop delivery). Both = either."
                />
              </div>
            ) : null}

            {activeTab === 'media' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Media</h3>
                </div>
                <KitchenMediaEditor
                  entityType="store"
                  entityId={storeId}
                  canEdit={canEditSchedule}
                />
              </div>
            ) : null}

            {activeTab === 'owner' && embeddedInAdmin ? (
              <OwnerDetailsSection
                title="Owner Details"
                owner={ownerDetails}
                loading={ownerLoading}
                canEdit={canEdit}
                onSave={handleSaveOwner}
                emptyBody={<p className="restaurant-detail-message">No owner assigned yet.</p>}
              />
            ) : null}

            {activeTab === 'address' ? (
              <div className="restaurant-detail-card restaurant-detail-address-card" role="tabpanel">
                <div className="restaurant-detail-address-wrap">
                  <AddressTable
                    addresses={addresses}
                    onUpdate={handleAddressUpdate}
                    readOnly={!canEdit}
                    sectionTitle="Address"
                  />
                </div>
              </div>
            ) : null}

            {activeTab === 'taxes' ? <CompanyTaxesDisplay storeId={storeId} /> : null}

            {activeTab === 'orderNumbering' ? (
              <OrderNumberingDisplay storeId={storeId} />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  if (embeddedInAdmin) {
    return page;
  }

  return (
    <RestaurantDashboardLayout
      activeTab="business-profile"
      enabledServiceTypes={enabledServiceTypes}
      servicesLoading={servicesLoading}
    >
      {page}
    </RestaurantDashboardLayout>
  );
};

export default StoreDetail;
